import { Card } from 'antd';
import { groupBy, union, sumBy, sortBy, filter } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';
import { segmentSortSeqMap, buSortMap } from '../../contexts/provider/toshiba.provider';
import { KPI_CODE } from '../../enum/toshiba.enum';
import { useNavigate } from 'react-router-dom';
import { ToshibaContext } from '../../contexts/toshiba.context';
import { useSnapshot } from '../../hooks/useSnapshot';
import { AppContext } from '../../contexts/AppContext';
import { NAME_PATH, useTralitSetting } from '../../hooks/useTralitSetting';
import ContentChart from '../base/content-chart';

type ChartProps = { dataProvider: any; loading: boolean; curDrillFieldKey: string };

const PotentialChart: React.FC<ChartProps> = (props) => {
  const { dataProvider, loading, curDrillFieldKey } = props;
  const { color } = useContext(AppContext);
  const { kpiValue, kpiUnitLabel, drillFields, setDrillFields, filter, setFilter, getAggregationPeriod } = useContext(ToshibaContext);
  const { currentSnapshot, prefSnapshot, getMonthLabel } = useSnapshot();
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });

  const [harvest, setHarvest] = useState('harvestPeriod');

  const filterMethod = useCallback(
    () => (item) => {
      return drillFields.every((drillField) => drillField.value.includes(item[drillField.fieldKey]));
    },
    [drillFields, currentSnapshot, filter]
  );

  const toolTipFormatter = (params) => {
    return `${params
      .map((param) => {
        if (['placeholder', '总数'].includes(param.seriesName) || !param.value) {
          return '';
        }
        return `<div><span>${param.marker}</span> <span>${param.seriesName}: </span><span>${kpiValue(
          KPI_CODE.SALES_AMOUNT,
          param.value,
          false,
          {
            [KPI_CODE.SALES_AMOUNT]: {
              unitValue: 100000000,
              unitLabel: '億円',
              precision: 2,
            },
          }
        )}</span>${
          param.data.segments
            ? `<br />${param.data.segments
                .map(
                  ({ segment, prev, cur }) =>
                    `<span style="padding-left: 18px">${segment}: ${kpiValue(KPI_CODE.SALES_AMOUNT, cur - prev, false, {
                      [KPI_CODE.SALES_AMOUNT]: {
                        unitValue: 100000000,
                        unitLabel: '億円',
                        precision: 2,
                      },
                    })}</span>`
                )
                .join('<br />')}`
            : ''
        }</div>`;
      })
      .join('')}
      `;
  };

  const chartOptions = useMemo(() => {
    const { potentialDiff: orgPotentialDiff } = dataProvider;

    let data = orgPotentialDiff.filter(filterMethod());

    switch (harvest) {
      case 'harvestPeriod':
        data = data.reduce((org, cur) => {
          const harvestPeriods = cur.harvestPeriod;
          if (harvestPeriods) {
            harvestPeriods.split(',').forEach((harvestPeriod) => {
              const period = harvestPeriod.trim();
              org.push({
                ...cur,
                harvestPeriod: period,
              });
            });
          } else {
            org.push(cur);
          }
          return org;
        }, []);
        break;
    }

    const gbCategory = groupBy(data, harvest);
    let drillFieldValues = union([...Object.keys(gbCategory)]);

    const targetTotal = sumBy(data, 'prev');
    const dataTotal = sumBy(data, 'cur');

    const compareData = drillFieldValues.reduce(
      (org, fieldValue) => {
        const dataCompare = data.filter((item) => item[harvest] === fieldValue);
        const gbSegment = groupBy(dataCompare, 'segment');
        const targetCompareTotal = sumBy(dataCompare, 'prev');
        const dataCompareTotal = sumBy(dataCompare, 'cur');
        if (dataCompareTotal > targetCompareTotal) {
          org.increase.push({
            value: dataCompareTotal - targetCompareTotal,
            segments: Object.keys(gbSegment).map((segment) => ({
              segment,
              prev: sumBy(gbSegment[segment], 'prev'),
              cur: sumBy(gbSegment[segment], 'cur'),
            })),
          });
          org.decrease.push({
            value: 0,
            segments: [],
          });
          org.placeholder.push(org.lastValue);
          org.lastValue = org.lastValue + (dataCompareTotal - targetCompareTotal);
          org.fieldValues.push(fieldValue);
        } else if (dataCompareTotal < targetCompareTotal) {
          org.increase.push({
            value: 0,
            segments: [],
          });
          org.decrease.push({
            value: targetCompareTotal - dataCompareTotal,
            segments: Object.keys(gbSegment).map((segment) => ({
              segment,
              prev: sumBy(gbSegment[segment], 'prev'),
              cur: sumBy(gbSegment[segment], 'cur'),
            })),
          });
          org.placeholder.push(org.lastValue - (targetCompareTotal - dataCompareTotal));
          org.lastValue = org.lastValue - (targetCompareTotal - dataCompareTotal);
          org.fieldValues.push(fieldValue);
        }
        return org;
      },
      {
        fieldValues: [],
        lastValue: targetTotal,
        placeholder: [],
        increase: [],
        decrease: [],
      }
    );

    const getFieldValues = () => {
      return sortBy(compareData.fieldValues);
    };

    const xAxisData = [
      `比較断面(${getMonthLabel(filter.targetSnapshot)})`,
      ...getFieldValues(),
      `今月断面(${getMonthLabel(currentSnapshot)})`,
    ];

    return {
      title: {
        value: '',
        styles: {
          fontSize: fontSize.chartTitle,
          margin: 0,
        },
      },
      tooltips: ['zoomIn', 'reset'],
      chartOptions: {
        textStyle: {
          fontFamily:
            "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,'Noto Sans',sans-serif,'Apple Color Emoji','Segoe UI Emoji','Segoe UI Symbol','Noto Color Emoji'",
        },
        animation: false,
        dataZoom: [
          {
            type: 'slider',
            yAxisIndex: 0,
            filterMode: 'none',
            showDetail: false,
          },
          {
            type: 'inside',
            yAxisIndex: 0,
            filterMode: 'none',
          },
        ],
        xAxis: [
          {
            type: 'category',
            nameGap: 10,
            axisLabel: {
              interval: 0,
              hideOverlap: false,
              fontSize: fontSize.chart,
              // formatter: (value) => dayjs(value).format(dimension.format),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },

            data: xAxisData,
          },
        ],
        yAxis: [
          {
            type: 'value',
            name: `値(億円)`,
            nameGap: 10,
            axisLabel: {
              fontSize: fontSize.chart,
              formatter: (value) => kpiValue(KPI_CODE.SALES_AMOUNT, value),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },
            scale: true,
            // min: minY,
          },
        ],
        grid: {
          top: 45,
          right: 60,
          bottom: 40,
          left: 85,
        },
        legend: {
          data: ['予算', ...getFieldValues().filter((value) => value), '超過分', '未達分'],
          selectedMode: false,
          textStyle: {
            fontSize: fontSize.chart,
          },
        },
        tooltip: {
          trigger: 'axis',
          appendToBody: true,
          axisPointer: {
            lineStyle: {
              width: 2,
            },
          },
          formatter: toolTipFormatter,
        },
        series: [
          {
            id: 'placeholder',
            name: 'placeholder',
            type: 'bar',
            stack: 'drill',
            itemStyle: {
              borderColor: 'transparent',
              color: 'transparent',
            },
            emphasis: {
              itemStyle: {
                borderColor: 'transparent',
                color: 'transparent',
              },
            },
            xAxisIndex: 0,
            data: [0, ...compareData.placeholder, 0],
            label: {
              show: false,
            },
            barMaxWidth: 50,
          },
          ...drillFieldValues.map((drillFieldValue) => {
            const data = gbCategory[drillFieldValue];
            const tarValue = sumBy(data, 'prev');
            const curValue = sumBy(data, 'cur');
            return {
              name: drillFieldValue,
              type: 'bar',
              stack: 'drill',
              data: [tarValue, ...compareData.fieldValues.map(() => 0), curValue],
              label: {
                show: false,
              },
              barMaxWidth: 50,
            };
          }),
          {
            id: 'decrease',
            name: '未達分',
            type: 'bar',
            stack: 'drill',
            itemStyle: {
              color: color.errorColor,
            },
            xAxisIndex: 0,
            data: [
              0,
              ...compareData.decrease.map(({ value, segments }) => ({
                value,
                show: true,
                segments,
              })),
              0,
            ],
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => (params.value && params.data.show ? `-${kpiValue(KPI_CODE.SALES_AMOUNT, params.value)}` : ''),
              color: color.errorColor,
            },
            barMaxWidth: 50,
          },
          {
            id: 'increase',
            name: '超過分',
            type: 'bar',
            stack: 'drill',
            itemStyle: {
              color: color.successColor,
            },
            xAxisIndex: 0,
            data: [
              0,
              ...compareData.increase.map(({ value, segments }) => ({
                value,
                show: true,
                segments,
              })),
              0,
            ],
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => (params.value && params.data.show ? `+${kpiValue(KPI_CODE.SALES_AMOUNT, params.value)}` : ''),
              color: color.successColor,
            },
            barMaxWidth: 50,
          },
          {
            name: '总数',
            type: 'bar',
            stack: 'drill',
            data: [{ value: 1, show: targetTotal }, ...compareData.fieldValues.map(() => 0), { value: 1, show: dataTotal }],
            label: {
              show: true,
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => {
                return params.data.show ? kpiValue(KPI_CODE.SALES_AMOUNT, params.data.show) : '';
              },
            },
            itemStyle: {
              color: 'transparent', // 使其不可见
            },
            emphasis: {
              itemStyle: {
                color: 'transparent', // 鼠标悬停时也透明
              },
            },
            silent: true, // 禁用鼠标交互
          },
        ],
      },
      height: document.body.clientHeight / 3,
    };
  }, [dataProvider, filter, harvest, curDrillFieldKey]);
  return (
    <Card styles={{ body: { minHeight: document.body.clientHeight / 3 + 20, padding: 0 } }}>
      <ContentChart
        contentList={[]}
        {...chartOptions}
        showSeriesLabel
        mouseWheelZoomLock={false}
        loading={loading}
        tooltips={['zoomIn']}
        isBank={!dataProvider || !dataProvider.data || dataProvider.data.length === 0}
      />
    </Card>
  );
};

export default PotentialChart;
